import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { motion, Variants } from "framer-motion";

import GavelIcon from "@mui/icons-material/Gavel";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SecurityIcon from "@mui/icons-material/Security";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PublicIcon from "@mui/icons-material/Public";
import CopyrightIcon from "@mui/icons-material/Copyright";
import PageMeta from '../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

const sections = [
  {
    icon: <AccountCircleIcon fontSize="small" />,
    title: "Accounts & access",
    body: `By creating an account on The Quizzler, you agree to provide accurate information and keep your login credentials secure. You are responsible for activity that occurs under your account. We reserve the right to suspend or remove accounts involved in abuse, cheating, spam, impersonation, or other harmful behavior.`,
  },
  {
    icon: <SecurityIcon fontSize="small" />,
    title: "Acceptable use",
    body: `You agree not to misuse the site or interfere with its operation. This includes attempting to exploit bugs, automate gameplay unfairly, scrape content, attack the service, bypass security measures, or disrupt the experience for other users. We may limit or block access to protect the integrity of the platform.`,
  },
  {
    icon: <CopyrightIcon fontSize="small" />,
    title: "Intellectual property",
    body: `Original quizzes, branding, site design, and custom content created for The Quizzler belong to the site unless otherwise stated. Harry Potter and related names, characters, locations, and imagery are the property of their respective copyright and trademark owners.`,
  },
  {
    icon: <PublicIcon fontSize="small" />,
    title: "Fan project disclaimer",
    body: `The Quizzler is an unofficial fan-made project created for entertainment and community purposes. It is not affiliated with, endorsed by, or connected to J.K. Rowling, Warner Bros. Entertainment, Wizarding World Digital, or any official Harry Potter rights holders.`,
  },
  {
    icon: <WarningAmberIcon fontSize="small" />,
    title: "Service availability",
    body: `We strive to keep The Quizzler available and functioning smoothly, but we cannot guarantee uninterrupted access or error-free operation. Features, quizzes, leaderboards, and account systems may change over time without notice. Use of the site is at your own risk.`,
  },
];

export default function TermsPage() {
  return (
    <>
      <PageMeta
        title="Terms & Conditions | The Quizzler"
        description="Review the terms and conditions for using The Quizzler, a fan-made Harry Potter trivia experience."
        canonical="https://thequizzler.app/terms"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{ letterSpacing: "-0.5px" }}
          >
            Terms & Conditions
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Rules, responsibilities, and guidelines for using The Quizzler.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Last updated: May 13, 2026
          </Typography>

          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Content sections */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            mb: 5,
          }}
        >
          {sections.map((section, i) => (
            <motion.div
              key={section.title}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    mt: "2px",
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    bgcolor: "#ef6c00",
                    "& svg": { color: "white" },
                  }}
                >
                  {section.icon}
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                  >
                    {section.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                  >
                    {section.body}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* Legal footer */}
        <motion.div
          custom={sections.length}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "action.hover",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <GavelIcon fontSize="small" color="action" />

              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Legal Notice
              </Typography>

              <Chip
                label="Unofficial Fan Site"
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.8 }}
            >
              The Quizzler is an independent fan-made trivia experience and is
              intended for entertainment purposes only. All Harry Potter-related
              names, characters, trademarks, and associated intellectual property
              belong to their respective owners.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.8, mt: 1.5 }}
            >
              By continuing to use this site, you agree to these Terms &
              Conditions. These terms may be updated periodically as the platform
              evolves.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </>
  );
}